import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader2, CheckCircle, AlertCircle, Mail, MapPin, Clock } from 'lucide-react';
import { submitPublicSupport } from '../../services/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await submitPublicSupport(formData);
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setError('Failed to send your message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 md:p-12 lg:p-16" data-testid="contact-page">
      <div className="max-w-4xl">
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6">
          Contact Us
        </h1>

        <p className="text-lg text-gray-700 leading-relaxed mb-8">
          Have a question about St David (University Lodge) No. 36 or interested in learning more 
          about Freemasonry? We'd be happy to hear from you.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-gray-100" data-testid="contact-form-card">
              <CardHeader>
                <CardTitle className="font-heading text-xl">Send a Message</CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you as soon as possible.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {submitSuccess ? (
                  <div className="text-center py-8" data-testid="contact-success">
                    <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-6 w-6 text-emerald-600" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">
                      Message Sent Successfully
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Thank you for contacting us. We will respond to your enquiry shortly.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setSubmitSuccess(false)}
                      data-testid="send-another-btn"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                      <Alert variant="destructive" data-testid="contact-error">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        required
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john.smith@example.com"
                        required
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Please enter your message here..."
                        rows={5}
                        required
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Send Message
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Contact Info Sidebar */}
          <div className="space-y-4">
            <Card className="border-gray-100" data-testid="meeting-location-card">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Meeting Location</h3>
                    <p className="text-sm text-gray-600">
                      The Lodge of Edinburgh (Mary's Chapel) No. 1<br />
                      19 Hill Street<br />
                      Edinburgh, EH2 3JP
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-100" data-testid="meeting-times-card">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">Meeting Times</h3>
                    <p className="text-sm text-gray-600">
                      Third Saturday of each month<br />
                      10:30 AM (September - April)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-100" data-testid="general-enquiries-card">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-900 mb-1">General Enquiries</h3>
                    <p className="text-sm text-gray-600">
                      For general enquiries, please use the contact form or visit us at one of our meetings.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
